import React from 'react';
import { ChevronDownIcon } from './icons';
import { cx, FOCUS_RING } from './styles';

/**
 * Titled section container. Generalises the `SectionHeader` pattern ProfilePage
 * already hand-rolls, so a page reads as a set of named groups instead of a
 * stack of visually identical cards you have to read to tell apart.
 *
 * `collapsible` uses native `<details>`/`<summary>`, which is keyboard- and
 * screen-reader-correct for free.
 */
export interface PanelProps {
  title?: React.ReactNode;
  /** Secondary line under the title — counts, period hints. */
  hint?: React.ReactNode;
  /** Right-aligned header controls. */
  actions?: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /**
   * Fires on every native `<details>` toggle. Only needed when a caller wants to
   * persist the disclosure; `defaultOpen` stays the single source of truth, so a
   * caller that feeds its stored value back in keeps React and the DOM in step.
   */
  onToggle?: (open: boolean) => void;
  padding?: 'none' | 'sm' | 'md';
  /** Onboarding-tour anchor, rendered as `data-tour`. */
  tourId?: string;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}

const paddingMap = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
};

const Header: React.FC<Pick<PanelProps, 'title' | 'hint' | 'actions'>> = ({
  title,
  hint,
  actions,
}) => (
  <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-3">
    <div className="min-w-0">
      {title && <h2 className="text-sm font-semibold text-white">{title}</h2>}
      {hint && <p className="mt-0.5 text-xs text-white/40">{hint}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Panel: React.FC<PanelProps> = ({
  title,
  hint,
  actions,
  collapsible,
  defaultOpen = true,
  onToggle,
  padding = 'md',
  tourId,
  className,
  bodyClassName,
  children,
}) => {
  const shell = cx('rounded-2xl border border-white/[0.06] bg-white/[0.02]', className);
  const hasHeader = Boolean(title || hint || actions);

  if (collapsible) {
    return (
      <details
        data-tour={tourId}
        open={defaultOpen}
        onToggle={onToggle ? (e) => onToggle((e.currentTarget as HTMLDetailsElement).open) : undefined}
        className={cx(shell, 'group')}
      >
        <summary
          className={cx(
            'flex cursor-pointer list-none items-start gap-3 px-5 py-4 marker:content-none',
            FOCUS_RING,
          )}
        >
          <Header title={title} hint={hint} actions={actions} />
          <ChevronDownIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/40 transition-transform group-open:rotate-180" />
        </summary>
        <div className={cx('border-t border-white/[0.06]', paddingMap[padding], bodyClassName)}>
          {children}
        </div>
      </details>
    );
  }

  return (
    <section data-tour={tourId} className={shell}>
      {hasHeader && (
        <div className="flex items-start gap-3 border-b border-white/[0.06] px-5 py-4">
          <Header title={title} hint={hint} actions={actions} />
        </div>
      )}
      <div className={cx(paddingMap[padding], bodyClassName)}>{children}</div>
    </section>
  );
};
